import { useState, useRef } from 'react'
import { BeanHead } from 'beanheads'
import { useAuth } from '@/context/auth-context'
import { enqueueSnackbar } from 'notistack'
import { useUpdateUserAvatar } from '@/lib/queries'
import { 
  type AvatarConfig, 
  AVATAR_OPTIONS, 
  DEFAULT_AVATAR_CONFIG, 
  randomizeAvatarConfig,
  convertSVGToBlob
} from '@/lib/avatar-utils'
import AvatarOption from './AvatarOption'
import Button from '@/components/Button'
import LoadingSpinner from '@/components/LoadingSpinner'

interface AvatarCustomizationModalProps {
  isOpen: boolean
  onClose: () => void
}

const AvatarCustomizationModal = ({ isOpen, onClose }: AvatarCustomizationModalProps) => {
  const { user } = useAuth()
  const avatarRef = useRef<HTMLDivElement>(null)
  const [config, setConfig] = useState<AvatarConfig>(DEFAULT_AVATAR_CONFIG)
  const [activeTab, setActiveTab] = useState<'appearance' | 'style' | 'accessories'>('appearance')

  const updateAvatarMutation = useUpdateUserAvatar()

  const handleSave = async () => {
    if (!avatarRef.current || !user) return
    
    try {
      const blob = await convertSVGToBlob(avatarRef.current)
      updateAvatarMutation.mutate(
        { userId: user.id, avatarBlob: blob },
        {
          onSuccess: () => {
            enqueueSnackbar('Avatar updated successfully!', { variant: 'success' })
            onClose()
          },
          onError: (error) => {
            console.error('Avatar update error:', error)
            enqueueSnackbar('Failed to update avatar', { variant: 'error' })
          },
        }
      )
    } catch (error) {
      console.error('Failed to convert avatar:', error)
      enqueueSnackbar('Failed to process avatar', { variant: 'error' })
    }
  }

  const handleRandomize = () => {
    setConfig(randomizeAvatarConfig())
  }

  const handleReset = () => {
    setConfig(DEFAULT_AVATAR_CONFIG)
  }

  const updateConfig = (key: keyof AvatarConfig, value: string | boolean) => {
    setConfig(prev => ({ ...prev, [key]: value }))
  }

  const tabs = [
    { key: 'appearance' as const, label: 'Appearance' },
    { key: 'style' as const, label: 'Style' },
    // Hidden for future unlock feature
    // { key: 'accessories' as const, label: 'Accessories' },
  ]

  const appearanceOptions = [
    { key: 'skinTone' as const, title: 'Skin Tone', options: AVATAR_OPTIONS.skinTone },
    { key: 'eyes' as const, title: 'Eyes', options: AVATAR_OPTIONS.eyes },
    { key: 'eyebrows' as const, title: 'Eyebrows', options: AVATAR_OPTIONS.eyebrows },
    { key: 'mouth' as const, title: 'Mouth', options: AVATAR_OPTIONS.mouth },
    { key: 'lashes' as const, title: 'Lashes', options: 'boolean' as const },
    { key: 'lipColor' as const, title: 'Lip Color', options: AVATAR_OPTIONS.lipColor },
  ]

  const styleOptions = [
    { key: 'hair' as const, title: 'Hair', options: AVATAR_OPTIONS.hair },
    { key: 'hairColor' as const, title: 'Hair Color', options: AVATAR_OPTIONS.hairColor },
    { key: 'facialHair' as const, title: 'Facial Hair', options: AVATAR_OPTIONS.facialHair },
    { key: 'clothing' as const, title: 'Clothing', options: AVATAR_OPTIONS.clothing },
    { key: 'clothingColor' as const, title: 'Clothing Color', options: AVATAR_OPTIONS.clothingColor },
    { key: 'body' as const, title: 'Body', options: AVATAR_OPTIONS.body },
  ]

  const accessoryOptions = [
    { key: 'accessory' as const, title: 'Glasses', options: AVATAR_OPTIONS.accessory },
    { key: 'hat' as const, title: 'Hat', options: AVATAR_OPTIONS.hat },
    { key: 'hatColor' as const, title: 'Hat Color', options: AVATAR_OPTIONS.hatColor },
    { key: 'mask' as const, title: 'Mask', options: 'boolean' as const },
    { key: 'faceMask' as const, title: 'Face Mask', options: 'boolean' as const },
    { key: 'graphic' as const, title: 'Graphic', options: AVATAR_OPTIONS.graphic },
    { key: 'circleColor' as const, title: 'Background', options: AVATAR_OPTIONS.circleColor },
  ]

  const getCurrentOptions = () => {
    switch (activeTab) {
      case 'appearance':
        return appearanceOptions
      case 'style':
        return styleOptions
      case 'accessories':
        return accessoryOptions
      default:
        return []
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal modal-open">
      <div className="modal-box w-11/12 max-w-5xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-bold text-lg">Customize Your Avatar</h3>
          <button
            className="btn btn-sm btn-circle btn-ghost"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Avatar Preview */}
          <div className="flex flex-col items-center">
            <div className="card bg-base-200 p-6 mb-4">
              <div ref={avatarRef} className="flex justify-center">
                <div style={{ width: '200px', height: '200px' }}>
                  <BeanHead
                    accessory={config.accessory}
                    body={config.body}
                    circleColor={config.circleColor}
                    clothing={config.clothing}
                    clothingColor={config.clothingColor}
                    eyebrows={config.eyebrows}
                    eyes={config.eyes}
                    facialHair={config.facialHair}
                    graphic={config.graphic}
                    hair={config.hair}
                    hairColor={config.hairColor}
                    hat={config.hat}
                    hatColor={config.hatColor}
                    lashes={config.lashes}
                    lipColor={config.lipColor}
                    mask={config.mask}
                    faceMask={config.faceMask}
                    mouth={config.mouth}
                    skinTone={config.skinTone}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRandomize}
              >
                Randomize
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
              >
                Reset
              </Button>
            </div>
          </div>

          {/* Customization Options */}
          <div className="flex flex-col">
            {/* Tabs */}
            <div className="tabs tabs-boxed mb-4">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  className={`tab ${activeTab === tab.key ? 'tab-active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Options */}
            <div className="flex-1 overflow-y-auto max-h-80">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {getCurrentOptions().map((option) => (
                  <AvatarOption
                    key={option.key}
                    title={option.title}
                    value={config[option.key]}
                    options={option.options}
                    onChange={(value) => updateConfig(option.key, value)}
                    config={config}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="modal-action">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={updateAvatarMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={updateAvatarMutation.isPending}
          >
            {updateAvatarMutation.isPending ? (
              <>
                <LoadingSpinner className="w-4 h-4" />
                Saving...
              </>
            ) : (
              'Save Avatar'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AvatarCustomizationModal